import { BlogCard } from '@blog/components/display/cards';
import { SubscribeModal } from '@blog/components/display/modals';
import { SkeletonGrid } from '@blog/components/display/skeletons';
import { Dropdown } from '@blog/components/utility/selects';
import {
    useCheckBlogSubscriptionQuery,
    useGetBlogPostsQuery,
    useGetCategoriesQuery,
    useLazyGetBlogPostsQuery,
} from '@blog/services';
import { mapCategoriestoName, sortCategories } from '@blog/utils/blog';
import { SelectOption } from '@common/types/common';
import { useAuth } from '@hooks/useAuth';
import { useDebounce } from '@hooks/useDebounce';
import Search from '@mui/icons-material/Search';
import { Button, InputAdornment, SelectChangeEvent, Tab, Tabs, TextField, Typography } from '@mui/material';
import { ChangeEventHandler, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
enum BLOG_TABS {
    LATEST,
    MOST_READ,
}

export const Blog = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);
    const [morePostsFetching, setMorePostsFetching] = useState(false);
    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

    const { user } = useAuth();

    const handleModalOpen = useCallback(() => setSubscribeModalOpen(true), []);
    const handleModalClose = useCallback(() => setSubscribeModalOpen(false), []);

    const [lazyGetBlogPosts] = useLazyGetBlogPostsQuery();
    const { data: subscriptionData, isLoading: subscriptionDataLoading } = useCheckBlogSubscriptionQuery(
        user?.email ?? '',
        { skip: !Boolean(user?.email) }
    );

    const resetPage = useCallback(() => setPage(1), []);

    const { activeTab, activeCategory, searchQuery } = useMemo(() => {
        return {
            activeTab: Number(searchParams.get('tab')) ?? BLOG_TABS.LATEST,
            activeCategory: Number(searchParams.get('category')),
            searchQuery: searchParams.get('search') ?? '',
        };
    }, [searchParams]);

    const debouncedSearchQuery = useDebounce({
        value: searchQuery,
        delay: 300,
        onDebounce: resetPage,
    });

    const intl = useIntl();

    const { data: postData, isFetching: postsFetching } = useGetBlogPostsQuery({
        categories: activeCategory !== 0 ? activeCategory : undefined,
        search: debouncedSearchQuery,
        orderby: activeTab === 1 ? 'popular_30_day_stats' : undefined,
    });
    const { data: blogCategories } = useGetCategoriesQuery();

    const showMorePosts = useCallback(async () => {
        setMorePostsFetching(true);
        await lazyGetBlogPosts({
            page: page + 1,
            categories: activeCategory !== 0 ? activeCategory : undefined,
            search: debouncedSearchQuery,
            orderby: activeTab === 1 ? 'popular_30_day_stats' : undefined,
        });
        setPage((prev) => prev + 1);
        setMorePostsFetching(false);
    }, [activeCategory, debouncedSearchQuery, lazyGetBlogPosts, page, activeTab]);

    const { posts, totalPages } = useMemo(() => {
        const posts = postData?.data ?? [];
        const totalPages = postData?.total_pages ?? null;
        return { posts, totalPages };
    }, [postData]);

    const showViewMoreButton = useMemo(() => {
        if (totalPages !== null) {
            return page < totalPages;
        }
        return true;
    }, [page, totalPages]);

    const dropdownOptions = useMemo<SelectOption<number>[]>(() => {
        if (blogCategories) {
            return [
                { value: 0, label: 'All' }, // This is left untranslated on purpose, as blogCategories isn't currently being translated due to belonging to the CMS API, which doesn't support translations (AFAIK, by Marco)
                ...blogCategories.map((category) => ({
                    value: category.id,
                    label: category.name,
                })),
            ];
        }
        return [];
    }, [blogCategories]);

    const handleTab = useCallback(
        (_: SyntheticEvent, value: number) => {
            if (value === 0) {
                searchParams.delete('tab');
            } else {
                searchParams.set('tab', String(value));
            }
            resetPage();
            setSearchParams(searchParams);
        },
        [searchParams, setSearchParams, resetPage]
    );

    const handleCategory = useCallback(
        (e: SelectChangeEvent<unknown>) => {
            const value = Number(e.target.value);
            if (value === 0) {
                searchParams.delete('category');
            } else {
                searchParams.set('category', String(value));
            }
            resetPage();
            setSearchParams(searchParams);
        },
        [searchParams, setSearchParams, resetPage]
    );

    const handleSearchQuery: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
        (e) => {
            const value = e.target.value;
            if (value) {
                searchParams.set('search', value);
            } else {
                searchParams.delete('search');
            }
            setSearchParams(searchParams);
        },
        [searchParams, setSearchParams]
    );

    // Move the active category to the top of the list
    const sortedCategories = useMemo(() => {
        if (blogCategories) {
            return sortCategories(activeCategory, blogCategories);
        }
        return [];
    }, [activeCategory, blogCategories]);

    return (
        <>
            <title>
                {intl.formatMessage({ id: 'blog.browserTitle', defaultMessage: 'Blog - Victron Professional' })}
            </title>
            <div className="px-4 md:px-8 pt-11 pb-20">
                <div className="flex">
                    <Typography variant="h3" className="font-museo text-3xl lg:text-5xl font-bold mb-10">
                        <FormattedMessage id="blog.overview" defaultMessage="Blog Overview" />
                    </Typography>
                </div>
                {
                    <>
                        <div className="flex flex-col sm:flex-row mb-10 gap-6">
                            <div className="flex flex-col sm:flex-row-reverse gap-6">
                                <TextField
                                    size="small"
                                    placeholder={intl.formatMessage({ id: 'common.search', defaultMessage: 'Search' })}
                                    value={searchQuery}
                                    onChange={handleSearchQuery}
                                    slotProps={{
                                        input: {
                                            // Match the border radius to the dropdown's
                                            className: 'text-sm md:text-base h-full rounded-[0.75rem]',
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <Dropdown items={dropdownOptions} value={activeCategory} onChange={handleCategory} />
                            </div>

                            <Button
                                color={subscriptionData?.subscribed ? 'neutral-700' : undefined}
                                className="font-medium h-fit rounded-full sm:ml-auto"
                                onClick={handleModalOpen}
                                disabled={subscriptionDataLoading}
                            >
                                {subscriptionData?.subscribed ? (
                                    <FormattedMessage id="common.unsubscribe" defaultMessage="Unsubscribe" />
                                ) : (
                                    <FormattedMessage id="common.subscribe" defaultMessage="Subscribe" />
                                )}
                            </Button>
                        </div>
                        <Tabs
                            value={activeTab}
                            onChange={handleTab}
                            className="border-b-2"
                            sx={{ '& button[aria-selected="true"]>p': { fontWeight: 500 } }}
                            slotProps={{
                                indicator: {},
                            }}
                        >
                            <Tab
                                label={
                                    <Typography color="black.main">
                                        <FormattedMessage id="blog.latestPosts" defaultMessage="Latest Posts" />
                                    </Typography>
                                }
                                className="capitalize bg-transparent "
                            />
                            <Tab
                                label={
                                    <Typography color="black.main">
                                        <FormattedMessage id="blog.mostRead" defaultMessage="Most Read Posts" />
                                    </Typography>
                                }
                                className="capitalize bg-transparent"
                            />
                        </Tabs>
                        {/* Keep the currently loaded posts if we're fetching additional ones */}
                        {(morePostsFetching || !postsFetching) && blogCategories && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 mt-8">
                                {posts.map((post) => (
                                    <BlogCard
                                        author={post.author}
                                        categories={mapCategoriestoName(post.categories, sortedCategories)}
                                        date={new Date(post.date)}
                                        slug={post.slug}
                                        summary={post.excerpt}
                                        title={post.title}
                                        image={post.image}
                                        key={post.id}
                                    />
                                ))}
                            </div>
                        )}

                        {postsFetching && <SkeletonGrid count={6} />}
                        {!postsFetching && posts && posts.length === 0 && (
                            <p className="font-inter font-neutral-900 bg-blog-neutral-300 w-fit mx-auto mt-32 lg:mt-64 p-4">
                                <FormattedMessage id="blog.noPosts" defaultMessage="No blog posts have been found" />
                            </p>
                        )}
                        {showViewMoreButton && (
                            <Button
                                onClick={showMorePosts}
                                variant="outlined"
                                color="black"
                                className="block w-full lg:w-auto lg:ml-auto mt-10 rounded-full font-medium"
                            >
                                <FormattedMessage id="blog.viewMorePosts" defaultMessage="View More Posts" />
                            </Button>
                        )}
                    </>
                }
            </div>
            {subscribeModalOpen && (
                <SubscribeModal
                    subscribed={subscriptionData?.subscribed}
                    open={subscribeModalOpen}
                    onClose={handleModalClose}
                />
            )}
        </>
    );
};
